import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { from, switchMap, of, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * ════════════════════════════════════════════════════════════════
 * ID TOKEN DE FIREBASE EN CADA PETICIÓN
 *
 * Hasta ahora Angular no enviaba ninguna credencial: el backend
 * recibía iIdUsuario en el body y le creía. Este interceptor adjunta
 * el ID token para que el backend pueda verificar criptográficamente
 * quién pregunta.
 *
 * getIdToken() devuelve el token en caché y lo renueva solo cuando
 * está por expirar, así que no hay costo por petición.
 * ════════════════════════════════════════════════════════════════
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {

  const auth = inject(Auth);
  const router = inject(Router);

  // Solo se manda el token a nuestra API, nunca a terceros
  if (!req.url.startsWith(environment.apiURL)) {
    return next(req);
  }

  const user = auth.currentUser;

  // Si el token en caché falla (caduca cada hora), se reintenta forzando
  // la renovación antes de rendirse. Quedarse sin cabecera por un tropiezo
  // momentáneo de red significaría un 401 y una salida a login innecesaria.
  const token$ = user
    ? from(user.getIdToken()).pipe(
        catchError(() => from(user.getIdToken(true))),
        catchError(err => {
          console.error(
            '[Auth] No se pudo obtener el ID token; la petición irá sin credencial',
            err
          );
          return of(null);
        })
      )
    : of(null);

  return token$.pipe(
    switchMap(token => {
      const peticion = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;

      return next(peticion);
    }),
    catchError(err => {
      // 401 = el backend ya no reconoce la sesión. Reautenticar.
      if (err?.status === 401) {
        router.navigate(['/login']);
      }

      return throwError(() => err);
    })
  );
};
